import { createQubo } from 'qubo'
import { isEmpty } from 'lodash'

import { maLogger } from '@/base/logger'

/**
 * Determines whether a user is allowed access to a specific page based on required access rules
 * and the user's active products and features. It prioritizes the products based on the order provided
 * in the `requiredAccess.products` array.
 *
 * @param {object} params - Parameters for access evaluation.
 * @param {object | boolean} [params.requiredAccess] - JSON-like rule object for evaluation or a boolean value.
 * @param {object[]} [params.userActiveProducts] - List of the user's active products.
 * @param {string} params.userActiveProducts[].name - Name of the active product (e.g., "ASO_BUSINESS", "COMPASS").
 * @param {string} params.userActiveProducts[].product - Product family (e.g., "ASO", "COMPASS").
 * @param {object[]} params.userActiveProducts[].featureSet - List of features associated with the product.
 * @param {string} params.userActiveProducts[].featureSet[].type - Type of the feature (e.g., "ACCESS_KEYWORD_INTELLIGENCE").
 *
 * @returns {boolean} - `true` if the user is allowed access based on the rule; otherwise `false`.
 *
 * @example
 * const requiredAccess = {
 *   products: ['COMPASS', 'ASO_BUSINESS'],
 *   rule: {
 *     $and: [
 *       { $eq: [KEYS.PRODUCT_FAMILY, 'COMPASS'] },
 *       { $in: [KEYS.PERMISSIONS, ['ACCESS_KEYWORD_INTELLIGENCE']] }
 *     ]
 *   }
 * };
 * const userActiveProducts = [
 *   {
 *     product: 'ASO',
 *     name: 'ASO_BUSINESS',
 *     featureSet: [{ type: 'ACCESS_KEYWORD_INTELLIGENCE' }]
 *   },
 *   {
 *     product: 'COMPASS',
 *     name: 'COMPASS',
 *     featureSet: [{ type: 'ACCESS_COMPASS_TRACE' }]
 *   }
 * ];
 *
 * const allowed = isUserAllowed({ requiredAccess, userActiveProducts });
 * console.log(allowed); // true or false based on the rule evaluation
 */
export function isUserAllowed({ requiredAccess: rule = {}, userActiveProducts = [] } = {}) {
  // If the rule is not provided, the user is allowed access.
  if (isEmpty(rule))
    return true

  try {
    const qubo = createQubo(userActiveProducts)
    const conditions = qubo.evaluate(rule)
    return conditions.some(isAllowed => isAllowed)
  }
  catch (error) {
    maLogger.warn(error)
    return false
  }
}
